"use client";

import React from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';
import { toAbsoluteMediaUrl, API_BASE } from '@/lib/media';
import { toCdn } from '@/lib/imageUrls';
import { scrubContactInfo } from '@/lib/scrubContact';

type Item = {
  id: number;
  title: string;
  thumbnailUrl: string;
  viewCount?: number | null;
  unsafe?: boolean;
};

function RelatedItem({ it, guest }: { it: Item; guest: boolean }) {
  const raw = String(it.thumbnailUrl || '').trim();
  const candidates = React.useMemo(() => {
    if (!raw) return [] as string[];
    const out: string[] = [];
    const isAbs = /^https?:\/\//i.test(raw);
    // Extract path if absolute so CDN builders don’t get a full URL
    let pathOnly = raw;
    if (isAbs) {
      try {
        const u = new URL(raw);
        pathOnly = u.pathname.replace(/^\/+/, '');
      } catch {
        pathOnly = raw.replace(/^https?:\/\/[^/]+\//i, '').replace(/^\/+/, '');
      }
    } else {
      pathOnly = raw.replace(/^\/+/, '');
    }
    // 1) As-is absolute first
    if (isAbs) out.push(raw);
    // 2) Feed CDN from path
    out.push(toCdn(pathOnly, 'feed'));
    // 3) Env-aware absolute from path
    out.push(toAbsoluteMediaUrl(pathOnly));
    // 4) API base + path (avoid root-relative fallback to prevent 404 html)
    if (API_BASE) out.push(`${API_BASE}/${pathOnly}`);
    return Array.from(new Set(out.filter(Boolean)));
  }, [raw]);
  const [idx, setIdx] = React.useState(0);
  // API sends correct blurred URLs for guests - just use as-is
  const src = candidates[idx] || '';
  const disableOpt = (process.env.NEXT_PUBLIC_IMAGE_UNOPTIMIZED === '1') || false;
  const isAwsPresign = /amazonaws\.com/i.test(src) && /X-Amz-/i.test(src);
  return (
    <Link href={`/video/${it.id}`} className="group flex gap-3">
      <div className="relative w-40 aspect-video rounded-lg overflow-hidden border">
        {src ? (
          <Image
            src={src}
            alt={it.title || 'Video thumbnail'}
            fill
            sizes="160px"
            className={`object-cover group-hover:opacity-95${guest && it.unsafe ? ' blur-xl scale-110' : ''}`}
            onError={() => setIdx(i => Math.min(i + 1, Math.max(0, candidates.length - 1)))}
            priority={false}
            unoptimized={disableOpt || isAwsPresign}
          />
        ) : (
          <div className="absolute inset-0 bg-card skeleton" />
        )}
        {guest && it.unsafe && (
          <div className="absolute inset-0 grid place-items-center">
            <span className="rounded-full bg-black/60 text-white px-3 py-1.5 inline-flex items-center gap-2 text-sm">Login to view</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium line-clamp-2 group-hover:underline">{scrubContactInfo(it.title)}</div>
        <div className="text-xs text-muted-foreground mt-1">{typeof it.viewCount === 'number' ? `${it.viewCount.toLocaleString()} views` : ''}</div>
      </div>
    </Link>
  );
}

export default function VideoRelatedClient({ excludeId }: { excludeId?: number | string }) {
  const [items, setItems] = React.useState<Item[] | null>(null);
  const { status } = useSession();
  const guest = status === 'unauthenticated';
  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // Canonical: v2 videos only (no legacy feed). No fallbacks.
        // Add cache-busting timestamp to prevent browser caching issues
        const cacheBust = Date.now();
        const vres = await fetch(`/api/video/v2?take=12&_=${cacheBust}`, { cache: 'no-store', signal: controller.signal, headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' } });
        if (!vres.ok) { setItems([]); return; }
        const list = await vres.json();
        const mapped = (Array.isArray(list) ? list : []).map((it: any) => ({
          id: Number(it.id ?? it.Id ?? 0),
          title: scrubContactInfo(String(it.title ?? it.Title ?? '')),
          thumbnailUrl: String(it.thumbnailUrl ?? it.ThumbnailUrl ?? ''),
          viewCount: typeof it.viewCount === 'number' ? it.viewCount : (typeof it.ViewCount === 'number' ? it.ViewCount : null),
          unsafe: Boolean(it.isUnsafeContent ?? it.IsUnsafeContent ?? false),
        })) as Item[];
        const filtered = mapped.filter(x => x.id && String(x.id) !== String(excludeId)).slice(0, 8);
        setItems(filtered);
      } catch { setItems([]); }
    })();
    return () => controller.abort();
  }, [excludeId]);

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold">Recommended</h3>
      <div className="flex flex-col gap-3">
        {items === null && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <div className="w-40 aspect-video rounded-lg border bg-card skeleton" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-5/6 rounded skeleton" />
              <div className="h-3 w-1/3 rounded skeleton" />
            </div>
          </div>
        ))}
        {Array.isArray(items) && items.map((it) => (
          <RelatedItem key={it.id} it={it} guest={guest} />
        ))}
      </div>
    </section>
  );
}
